import type { SubscriptionProduct } from '@forest/native-services'
import type { ServiceLevelStatus } from '@forest/paywall-core'
import { subscriptionRecord } from '~/lib/service-level'

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS
const COUNTDOWN_WINDOW_MS = 48 * HOUR_MS

/** What the loss screen needs from the expiring subscription. */
export interface ExpiringSubscription {
  expiresAtMs: number
  /**
   * Whether 24 hours or less were left at load, where the loss screen's
   * title says the benefits expire soon (GLOSSARY.md "Expiry countdown").
   * Read once here, so crossing 24 hours while the page stays open does not
   * change it.
   */
  isExpiringSoon: boolean
  /** Pro user (GLOSSARY.md): Pro benefits are kept for life. */
  isPremium: boolean
}

/**
 * The subscription the loss screen is for, an expiring subscriber's
 * (GLOSSARY.md): not yet expired, auto-renew still off. `null` means the loss
 * screen does not apply to this user.
 */
export function selectExpiringSubscription(
  status: ServiceLevelStatus,
  nowMs: number,
): ExpiringSubscription | null {
  const record = subscriptionRecord(status)
  if (!record) return null
  const expiresAtMs = Date.parse(record.expires_at)
  // An unparseable expiry reads as expired: NaN would otherwise slip past
  // the `<=` guard and render "Expires in NaN days".
  if (!(expiresAtMs > nowMs)) return null
  if (record.is_auto_renewing) return null
  return {
    expiresAtMs,
    isExpiringSoon: expiresAtMs - nowMs <= DAY_MS,
    isPremium: status.is_premium,
  }
}

/**
 * The plan an expiring subscriber (GLOSSARY.md) is on, as the plan picker's
 * footer names it.
 */
export interface CurrentPlan {
  duration: SubscriptionProduct['duration']
  expiresAtMs: number
}

/**
 * The expiring subscriber's current plan, told by its SKU among the store
 * products. `null` for a lapsed subscriber, or a SKU no store product lists.
 */
export function selectCurrentPlan(
  status: ServiceLevelStatus,
  storeProducts: SubscriptionProduct[],
  nowMs: number,
): CurrentPlan | null {
  const subscription = selectExpiringSubscription(status, nowMs)
  if (!subscription) return null
  const sku = subscriptionRecord(status)?.sku_id
  const product = storeProducts.find((p) => p.sku === sku)
  return product
    ? { duration: product.duration, expiresAtMs: subscription.expiresAtMs }
    : null
}

/**
 * Remaining time rounded up to whole days, never below 1: under 24 h reads 1,
 * and so does zero, where the expiry countdown holds and the page stays open.
 */
export function remainingDays(expiresAtMs: number, nowMs: number): number {
  return Math.max(1, Math.ceil((expiresAtMs - nowMs) / DAY_MS))
}

/**
 * Whether the expiry is within the final 48 hours, where the expiry countdown
 * (GLOSSARY.md) shows — and the title reads 2 days or fewer. Expiry included.
 */
export function inCountdownWindow(expiresAtMs: number, nowMs: number): boolean {
  return expiresAtMs - nowMs <= COUNTDOWN_WINDOW_MS
}

/**
 * The expiry countdown (GLOSSARY.md): `HH : MM : SS` to the expiry, only in
 * the final 48 hours (`null` before), holding at `00 : 00 : 00` from zero.
 */
export function expiryCountdown(
  expiresAtMs: number,
  nowMs: number,
): string | null {
  if (!inCountdownWindow(expiresAtMs, nowMs)) return null
  const remainingMs = expiresAtMs - nowMs
  // Rounded up, like the day count: it reads zero only once time is up.
  const seconds = Math.max(0, Math.ceil(remainingMs / 1000))
  return [
    Math.floor(seconds / 3600),
    Math.floor(seconds / 60) % 60,
    seconds % 60,
  ]
    .map((part) => String(part).padStart(2, '0'))
    .join(' : ')
}
